import { useState, useCallback, useEffect } from 'react';
import { IconArrowLeft, IconSearch, IconX, IconLoader2, IconSelector } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { formatDateTime } from '../utils/dateFormat';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface StoreOption {
  external_id: string;
  name: string;
}

interface BasketSearchResult {
  id: number;
  message_id: string;
  transaction_reference: string;
  basket_timestamp: string;
  store_id: string;
  store_name: string;
  till_id: string;
  employee_id: string;
  i_customer: number;
  customer_mobile_number: string;
  customer_card_number: string;
  basket_total_price: string;
  basket_total_discount: string;
  basket_tax: string;
  promo_data_allotted: number;
  status: string;
  remarks: string;
}

interface BasketProduct {
  id: number;
  gtin: string;
  description: string;
  quantity: number;
  price: string;
  discount_amount: number;
  discount_total: number;
}

interface BasketAllocation {
  id: number;
  rule_id: number;
  reward: string;
  total_reward: string;
  reward_count: number;
  spend_requirement: number;
  rule_title: string;
  rule_description: string;
  rule_type: string;
  rule_promo_data: number;
  rule_status: string;
}

interface BasketCustomer {
  i_customer: number;
  msisdn: string;
  carrier: string;
  city: string;
  suburb: string;
  is_active: boolean;
  active_subscription: boolean;
  suspended: boolean;
  blocked: boolean;
  churned: boolean;
  spend_30_day: number;
  spend_90_day: number;
  spend_ltv: number;
  device_type: string;
  app_version: string;
  brand: string;
  company_name: string;
}

interface BasketDetail {
  basket: BasketSearchResult & { datastream_metadata?: unknown };
  products: BasketProduct[];
  allocations: BasketAllocation[];
  customer: BasketCustomer | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_BADGE_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'info' | 'outline' | 'ghost'> = {
  completed: 'success',
  customer_not_found: 'warning',
  processing: 'info',
  failed: 'destructive',
};

function statusBadgeVariant(status: string) {
  return STATUS_BADGE_VARIANTS[status] ?? 'secondary';
}

function currency(value: string | number | undefined | null): string {
  if (value == null || value === '') return 'R 0.00';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return `R ${value}`;
  return `R ${num.toFixed(2)}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function BasketSearchPage() {
  // Store autocomplete
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [selectedStore, setSelectedStore] = useState<StoreOption | null>(null);
  const [storePopoverOpen, setStorePopoverOpen] = useState(false);

  // Search form state
  const [tillId, setTillId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [customerIdentifier, setCustomerIdentifier] = useState('');
  const [msisdn, setMsisdn] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Results state
  const [results, setResults] = useState<BasketSearchResult[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Detail view state
  const [detail, setDetail] = useState<BasketDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [selectedBasketId, setSelectedBasketId] = useState<number | null>(null);

  const hasSearchCriteria = !!(selectedStore || tillId || employeeId || customerIdentifier || msisdn || dateFrom || dateTo);

  useEffect(() => {
    let cancelled = false;
    setStoresLoading(true);
    api.get('/spar-mobile/basket-search/stores')
      .then((res) => {
        if (!cancelled) setStores(res.data.data ?? []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setStoresLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // ----- Search -----

  const handleSearch = useCallback(async () => {
    if (!hasSearchCriteria) return;

    setSearchError('');
    setSearchLoading(true);
    setPage(0);

    try {
      const params = new URLSearchParams();
      if (selectedStore) params.append('storeId', selectedStore.external_id);
      if (tillId) params.append('tillId', tillId);
      if (employeeId) params.append('employeeId', employeeId);
      if (customerIdentifier) params.append('customerIdentifier', customerIdentifier);
      if (msisdn) params.append('msisdn', msisdn);
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);

      const res = await api.get(`/spar-mobile/basket-search?${params.toString()}`);
      setResults(res.data.data ?? []);
    } catch (err: any) {
      setSearchError(err.response?.data?.message || err.message || 'Search failed.');
      setResults(null);
    } finally {
      setSearchLoading(false);
    }
  }, [selectedStore, tillId, employeeId, customerIdentifier, msisdn, dateFrom, dateTo, hasSearchCriteria]);

  const handleClear = () => {
    setSelectedStore(null);
    setTillId('');
    setEmployeeId('');
    setCustomerIdentifier('');
    setMsisdn('');
    setDateFrom('');
    setDateTo('');
    setResults(null);
    setSearchError('');
    setPage(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && hasSearchCriteria) {
      handleSearch();
    }
  };

  // ----- Detail -----

  const openDetail = async (basketId: number) => {
    setDetailError('');
    setDetailLoading(true);
    setSelectedBasketId(basketId);

    try {
      const res = await api.get(`/spar-mobile/basket-search/${basketId}`);
      setDetail(res.data.data);
    } catch (err: any) {
      setDetailError(err.response?.data?.message || err.message || 'Failed to load basket details.');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedBasketId(null);
    setDetail(null);
    setDetailError('');
  };

  // ----- Pagination helpers -----

  const totalPages = results ? Math.ceil(results.length / rowsPerPage) : 0;
  const paginatedResults = results?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) ?? [];

  // =========================================================================
  // DETAIL VIEW
  // =========================================================================

  if (selectedBasketId !== null) {
    if (detailLoading) {
      return (
        <div>
          <PageHeader title={`Basket #${selectedBasketId}`} subtitle="Loading..." />
          <div className="flex justify-center py-16">
            <IconLoader2 className="size-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      );
    }

    if (detailError) {
      return (
        <div>
          <PageHeader
            title={`Basket #${selectedBasketId}`}
            action={
              <Button variant="outline" onClick={closeDetail}>
                <IconArrowLeft className="size-4" />
                Back to results
              </Button>
            }
          />
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>{detailError}</AlertDescription>
          </Alert>
        </div>
      );
    }

    if (!detail) return null;

    const { basket, products, allocations, customer } = detail;

    const productTotals = products.reduce(
      (acc, p) => ({
        quantity: acc.quantity + p.quantity,
        price: acc.price + parseFloat(p.price || '0'),
        discountAmount: acc.discountAmount + p.discount_amount,
        discountTotal: acc.discountTotal + p.discount_total,
      }),
      { quantity: 0, price: 0, discountAmount: 0, discountTotal: 0 },
    );

    return (
      <div>
        <PageHeader
          title={`Basket #${basket.id}`}
          subtitle={`${basket.store_name} — ${formatDateTime(basket.basket_timestamp)}`}
          action={
            <Button variant="outline" onClick={closeDetail}>
              <IconArrowLeft className="size-4" />
              Back to results
            </Button>
          }
        />

        {/* Row 1: Basket Summary + Customer Info */}
        <div className="mb-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          <MainCard>
            <h3 className="mb-3 text-base font-semibold">Basket Summary</h3>
            <Separator className="mb-3" />
            <div className="flex flex-col gap-2.5">
              <DetailRow label="Store" value={`${basket.store_name} (${basket.store_id})`} />
              <DetailRow label="Timestamp" value={formatDateTime(basket.basket_timestamp)} />
              <DetailRow label="Transaction Ref" value={basket.transaction_reference} />
              <DetailRow label="Total Price" value={currency(basket.basket_total_price)} />
              <DetailRow label="Discount" value={currency(basket.basket_total_discount)} />
              <DetailRow label="Tax" value={currency(basket.basket_tax)} />
              <DetailRow label="Promo Data" value={`${basket.promo_data_allotted} MB`} />
              <div className="flex items-center gap-2">
                <span className="min-w-[120px] text-sm text-muted-foreground">Status</span>
                <Badge variant={statusBadgeVariant(basket.status)}>
                  {basket.status}
                </Badge>
              </div>
              {basket.remarks && <DetailRow label="Remarks" value={basket.remarks} />}
            </div>
          </MainCard>

          <MainCard>
            <h3 className="mb-3 text-base font-semibold">Customer Info</h3>
            <Separator className="mb-3" />
            {customer ? (
              <div className="flex flex-col gap-2.5">
                <DetailRow label="MSISDN" value={customer.msisdn} />
                <DetailRow label="Carrier" value={customer.carrier} />
                <DetailRow label="Location" value={[customer.city, customer.suburb].filter(Boolean).join(', ') || '-'} />
                <div className="flex items-center gap-2">
                  <span className="min-w-[120px] text-sm text-muted-foreground">Active</span>
                  <Badge variant={customer.is_active ? 'success' : 'destructive'}>
                    {customer.is_active ? 'Yes' : 'No'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="min-w-[120px] text-sm text-muted-foreground">Subscription</span>
                  <Badge variant={customer.active_subscription ? 'success' : 'secondary'}>
                    {customer.active_subscription ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <DetailRow label="Spend 30d" value={currency(customer.spend_30_day)} />
                <DetailRow label="Spend 90d" value={currency(customer.spend_90_day)} />
                <DetailRow label="Spend LTV" value={currency(customer.spend_ltv)} />
                <DetailRow label="Device" value={customer.device_type || '-'} />
                <DetailRow label="App Version" value={customer.app_version || '-'} />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No customer linked to this basket
              </p>
            )}
          </MainCard>
        </div>

        {/* Row 2: Products */}
        <MainCard className="mb-6">
          <h3 className="mb-3 text-base font-semibold">
            Products ({products.length} item{products.length !== 1 ? 's' : ''})
          </h3>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>GTIN</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Price</TableHead>
                  <TableHead className="text-right">Discount Amount</TableHead>
                  <TableHead className="text-right">Discount Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>{p.gtin}</TableCell>
                    <TableCell>{p.description}</TableCell>
                    <TableCell className="text-right">{p.quantity}</TableCell>
                    <TableCell className="text-right">{currency(p.price)}</TableCell>
                    <TableCell className="text-right">{currency(p.discount_amount)}</TableCell>
                    <TableCell className="text-right">{currency(p.discount_total)}</TableCell>
                  </TableRow>
                ))}
                {products.length > 0 && (
                  <TableRow className="font-bold">
                    <TableCell colSpan={2}>Totals</TableCell>
                    <TableCell className="text-right">{productTotals.quantity}</TableCell>
                    <TableCell className="text-right">{currency(productTotals.price)}</TableCell>
                    <TableCell className="text-right">{currency(productTotals.discountAmount)}</TableCell>
                    <TableCell className="text-right">{currency(productTotals.discountTotal)}</TableCell>
                  </TableRow>
                )}
                {products.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center">
                      <span className="py-4 text-sm text-muted-foreground">No products in this basket</span>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </MainCard>

        {/* Row 3: Promo Rule Allocations */}
        <MainCard>
          <h3 className="mb-3 text-base font-semibold">
            Promo Rule Allocations ({allocations.length} rule{allocations.length !== 1 ? 's' : ''})
          </h3>
          {allocations.length > 0 ? (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rule ID</TableHead>
                    <TableHead>Rule Title</TableHead>
                    <TableHead>Rule Type</TableHead>
                    <TableHead className="text-right">Reward (MB)</TableHead>
                    <TableHead className="text-right">Total Reward</TableHead>
                    <TableHead className="text-right">Reward Count</TableHead>
                    <TableHead className="text-right">Spend Requirement</TableHead>
                    <TableHead>Rule Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allocations.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.rule_id}</TableCell>
                      <TableCell>{a.rule_title}</TableCell>
                      <TableCell>{a.rule_type}</TableCell>
                      <TableCell className="text-right">{a.reward}</TableCell>
                      <TableCell className="text-right">{a.total_reward}</TableCell>
                      <TableCell className="text-right">{a.reward_count}</TableCell>
                      <TableCell className="text-right">{currency(a.spend_requirement)}</TableCell>
                      <TableCell>
                        <Badge variant={a.rule_status === 'active' ? 'success' : 'secondary'}>
                          {a.rule_status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No promo rules allocated to this basket
            </p>
          )}
        </MainCard>
      </div>
    );
  }

  // =========================================================================
  // SEARCH VIEW
  // =========================================================================

  return (
    <div>
      <PageHeader
        title="Basket Search"
        subtitle="Search for baskets by store name, till ID, employee ID, or date range"
      />

      {/* Search Form */}
      <MainCard className="mb-6">
        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {/* Store Name - Popover + Command combobox */}
          <div className="flex flex-col gap-1.5">
            <Label>Store Name</Label>
            <Popover open={storePopoverOpen} onOpenChange={setStorePopoverOpen}>
              <PopoverTrigger
                className={cn(
                  'flex h-8 w-full items-center justify-between rounded-lg border border-input bg-transparent px-2.5 text-sm transition-colors',
                  'focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50',
                  'dark:bg-input/30',
                  !selectedStore && 'text-muted-foreground',
                )}
              >
                <span className="truncate">
                  {selectedStore ? selectedStore.name : 'Select store...'}
                </span>
                <IconSelector className="ml-2 size-4 shrink-0 text-muted-foreground" />
              </PopoverTrigger>
              <PopoverContent className="w-[--anchor-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search stores..." />
                  <CommandList>
                    <CommandEmpty>
                      {storesLoading ? 'Loading stores...' : 'No store found.'}
                    </CommandEmpty>
                    <CommandGroup>
                      {stores.map((store) => (
                        <CommandItem
                          key={store.external_id}
                          value={store.name}
                          data-checked={selectedStore?.external_id === store.external_id}
                          onSelect={() => {
                            setSelectedStore(
                              selectedStore?.external_id === store.external_id ? null : store,
                            );
                            setStorePopoverOpen(false);
                          }}
                        >
                          {store.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tillId">Till ID</Label>
            <Input
              id="tillId"
              value={tillId}
              onChange={(e) => setTillId(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="employeeId">Employee ID</Label>
            <Input
              id="employeeId"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="customerIdentifier">Customer Identifier</Label>
            <Input
              id="customerIdentifier"
              value={customerIdentifier}
              onChange={(e) => setCustomerIdentifier(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="SPAR UUID"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="msisdn">MSISDN</Label>
            <Input
              id="msisdn"
              value={msisdn}
              onChange={(e) => setMsisdn(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. 27681234567"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dateFrom">Date & Time From</Label>
            <Input
              id="dateFrom"
              type="datetime-local"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dateTo">Date & Time To</Label>
            <Input
              id="dateTo"
              type="datetime-local"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            onClick={handleSearch}
            disabled={!hasSearchCriteria || searchLoading}
          >
            {searchLoading
              ? <IconLoader2 className="size-4 animate-spin" />
              : <IconSearch className="size-4" />}
            {searchLoading ? 'Searching...' : 'Search'}
          </Button>
          <Button variant="outline" onClick={handleClear}>
            <IconX className="size-4" />
            Clear
          </Button>
        </div>
      </MainCard>

      {/* Error */}
      {searchError && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center justify-between">
            {searchError}
            <Button variant="ghost" size="icon" onClick={() => setSearchError('')}>
              <IconX className="size-4" />
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Results Table */}
      {results !== null && (
        <MainCard>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold">
              Results ({results.length} basket{results.length !== 1 ? 's' : ''})
            </h3>
          </div>

          {results.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No results found
            </p>
          ) : (
            <>
              <div className="relative rounded-lg border">
                {searchLoading && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 dark:bg-black/30">
                    <IconLoader2 className="size-8 animate-spin text-muted-foreground" />
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Store Name</TableHead>
                      <TableHead>Basket Time</TableHead>
                      <TableHead>Till ID</TableHead>
                      <TableHead>Employee ID</TableHead>
                      <TableHead>Customer Mobile</TableHead>
                      <TableHead className="text-right">Total Price</TableHead>
                      <TableHead className="text-right">Data Allotted (MB)</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedResults.map((row) => (
                      <TableRow
                        key={row.id}
                        className="cursor-pointer"
                        onClick={() => openDetail(row.id)}
                      >
                        <TableCell>{row.store_name}</TableCell>
                        <TableCell>{formatDateTime(row.basket_timestamp)}</TableCell>
                        <TableCell>{row.till_id}</TableCell>
                        <TableCell>{row.employee_id}</TableCell>
                        <TableCell>{row.customer_mobile_number || '-'}</TableCell>
                        <TableCell className="text-right">{currency(row.basket_total_price)}</TableCell>
                        <TableCell className="text-right">{row.promo_data_allotted}</TableCell>
                        <TableCell>
                          <Badge variant={statusBadgeVariant(row.status)}>
                            {row.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between pt-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Rows per page:</span>
                  <Select
                    value={rowsPerPage}
                    onValueChange={(val) => {
                      setRowsPerPage(Number(val));
                      setPage(0);
                    }}
                  >
                    <SelectTrigger className="w-18">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[10, 25, 50, 100].map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>
                    {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, results.length)} of {results.length}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page === 0}
                    onClick={() => setPage(page - 1)}
                  >
                    <IconArrowLeft className="size-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(page + 1)}
                  >
                    <IconArrowLeft className="size-4 rotate-180" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </MainCard>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <span className="min-w-[120px] text-sm text-muted-foreground">
        {label}
      </span>
      <span className="text-sm">{value || '-'}</span>
    </div>
  );
}
